import {
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListSubheader,
  MenuItem,
  Popover,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { parseSafeInt } from "Backend";
import { Navigate, useNavigate, useParams } from "react-router";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  deleteCaseDisplayGroup,
  setCaseDisplayGroupTableItem,
  selectCalcProperties,
  selectCaseDisplayGroups,
  selectCaseProperties,
  updateCaseDisplayGroups,
  updateCaseDisplayGroupWithItems,
} from "redux/slices/lensEditor";
import { P } from "theme/typography";
import { useContext, useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { swapElements } from "helpers/drag-and-drop";
import {
  CaseDisplayGroup as CDG,
  CaseDisplayGroupItem,
  CaseDisplayGroupItemInput,
  CaseDisplayGroupType,
} from "generated/graphql";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import theme, { LeftStickyColumnSX } from "theme";
import { ArrowDropDown, ArrowDropUp, ArrowLeft, ArrowRight, Delete, DragIndicator, Key } from "@mui/icons-material";
import { useCaseCalc, useCaseProperty } from "./useCaseProperty";
import { ConfirmationDialogContext } from "contexts/ConfirmationDialogContext";
import { format, parseISO } from "date-fns";
import DescriptionTooltip from "components/FormField/DescriptionTooltip";
import { useSnackbar } from "notistack";
import PSelect from "components/PSelect";
import { DropdownButton } from "components/DropdownButton";
import { AppRoutes } from "AppRoutes";
import { usePopover } from "helpers/hooks/usePopover";

type ItemType = "calc" | "property";

function CasePropertyPreview({
  item,
  onRemove,
}: {
  item: CaseDisplayGroupItem;
  onRemove: (type: ItemType, key: string) => void;
}) {
  const caseProperty = useCaseProperty(item.casePropertyKey);

  if (caseProperty === undefined) {
    return <></>;
  }

  return (
    <Box sx={{ display: "flex", width: "100%", cursor: "pointer" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "flex-start", width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
          }}>
          <P variant="smBold" sx={{ color: caseProperty.isHidden ? theme.palette.grey[400] : undefined }}>
            {caseProperty.displayName}
          </P>
          <DescriptionTooltip description={caseProperty.description} />
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Chip color="primary" label={caseProperty.dataType} size="small" />
          <Chip
            label={caseProperty.key}
            size="small"
            icon={<Key sx={{ color: `${theme.palette.grey[300]}!important` }} />}
          />
        </Box>
        <P variant="xs">Is Invisible: {caseProperty.isHidden ? "Yes" : "No"}</P>
        <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <P variant="xs">Collect Visibility: {caseProperty.collectVisibility}</P>
          <P variant="xs">Created At: {format(parseISO(caseProperty.createdAt), "MM/dd/yyyy")}</P>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          position: "absolute",
          right: 16,
          gap: 1,
          top: 0,
        }}>
        <Button
          color="error"
          variant="text"
          onClick={(evt) => {
            evt.stopPropagation();
            onRemove("property", caseProperty.key);
          }}>
          Remove
        </Button>
      </Box>
    </Box>
  );
}

function CaseCalcPreview({
  item,
  onRemove,
}: {
  item: CaseDisplayGroupItem;
  onRemove: (type: ItemType, key: string) => void;
}) {
  const caseCalc = useCaseCalc(item.caseCalcKey);

  if (caseCalc === undefined) {
    return <></>;
  }

  return (
    <Box sx={{ display: "flex", width: "100%", cursor: "pointer" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "flex-start", width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
          }}>
          <P variant="smBold">{caseCalc.displayName}</P>
          <DescriptionTooltip description={caseCalc.description} />
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Chip color="primary" label={caseCalc.dataType} size="small" />
          <Chip
            label={caseCalc.key}
            size="small"
            icon={<Key sx={{ color: `${theme.palette.grey[300]}!important` }} />}
          />
          <Chip color="secondary" label="Calc" size="small" />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <P variant="xs">Collect Visibility: {caseCalc.isCollectVisible ? "visible" : "invisible"}</P>
          <P variant="xs">Created At: {format(parseISO(caseCalc.createdAt), "MM/dd/yyyy")}</P>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          position: "absolute",
          right: 16,
          gap: 1,
          top: 0,
        }}>
        <Button
          color="error"
          variant="text"
          onClick={(evt) => {
            evt.stopPropagation();
            onRemove("calc", caseCalc.key);
          }}>
          Remove
        </Button>
      </Box>
    </Box>
  );
}

function ItemTableCell({
  rowIndex,
  colIndex,
  group,
  lensId,
}: {
  rowIndex: number;
  colIndex: number;
  lensId: number;
  group: CDG;
}) {
  const itemInCell = group.items.find((i) => i.colIndex === colIndex && i.rowIndex === rowIndex);
  const calcSchemas = useAppSelector(selectCalcProperties);
  const casePropertySchemas = useAppSelector(selectCaseProperties);
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const handleItemChange: (evt: SelectChangeEvent<unknown>, child?: any) => void = async (
    evt: SelectChangeEvent<unknown>,
    child?: object
  ) => {
    const isCalc = (child as { props: any }).props["data-type"] === "calc";
    const key = (evt.target.value as string) !== "" ? (evt.target.value as string) : null;
    try {
      await dispatch(
        setCaseDisplayGroupTableItem({
          lensId,
          caseDisplayGroupId: group.id,
          rowIndex,
          colIndex,
          caseCalcPropertyKey: isCalc ? key : null,
          caseCustomPropertyKey: !isCalc ? key : null,
        })
      ).unwrap();
    } catch (e) {
      console.error(e);
      enqueueSnackbar("Unable to add item. It is probably already used in this table", { variant: "error" });
    }
  };

  const propertyAlreadyInUse = (propertyKey: string): boolean => {
    return !!group.items.find((i) => i.casePropertyKey === propertyKey);
  };
  const calcAlreadyInUse = (calcKey: string): boolean => {
    return !!group.items.find((i) => i.caseCalcKey === calcKey);
  };

  return (
    <TableCell>
      <FormControl size="small" fullWidth>
        <InputLabel variant="outlined">Select Item</InputLabel>
        <PSelect
          label="Select Firm"
          value={itemInCell?.caseCalcKey || itemInCell?.casePropertyKey || ""}
          onChange={handleItemChange}>
          <MenuItem key="none" value="">
            No Item
          </MenuItem>
          <ListSubheader>Properties</ListSubheader>
          {casePropertySchemas.map((item) => (
            <MenuItem
              key={item.key}
              data-type="prop"
              value={item.key}
              sx={{ color: propertyAlreadyInUse(item.key) ? theme.palette.grey[100] : undefined }}>
              {item.displayName}
            </MenuItem>
          ))}
          <ListSubheader>Calcs</ListSubheader>
          {calcSchemas.map((item) => (
            <MenuItem
              key={item.key}
              data-type="calc"
              value={item.key}
              sx={{ color: calcAlreadyInUse(item.key) ? theme.palette.grey[100] : undefined }}>
              {item.displayName}
            </MenuItem>
          ))}
        </PSelect>
      </FormControl>
    </TableCell>
  );
}

function CardTypeGroup({ group, lensId }: { group: CDG; lensId: number }) {
  const [displayItems, setDisplayItems] = useState<CaseDisplayGroupItem[]>([]);
  const caseProperties = useAppSelector(selectCaseProperties);
  const calcs = useAppSelector(selectCalcProperties);
  const buttonCaseProperties = [
    {
      value: "",
      key: "",
      title: "Properties",
      header: true,
    },
  ].concat(
    caseProperties
      .map((cp) => ({
        value: `prop-${cp.key}`,
        key: cp.key,
        title: cp.displayName,
        header: false,
      }))
      .filter((bcp) => !displayItems.find((di) => di.casePropertyKey === bcp.key))
  );
  const buttonCalcs = [{ value: "", key: "calcs", title: "Calcs", header: true }].concat(
    calcs
      .map((c) => ({
        value: `calc-${c.key}`,
        key: c.key,
        title: c.displayName,
        header: false,
      }))
      .filter((bc) => !displayItems.find((di) => di.caseCalcKey === bc.key))
  );
  const buttonItems = buttonCaseProperties.concat(buttonCalcs);
  const snackpatch = useAppDispatchWithSnackbar();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();

  const removeItem = async (type: ItemType, itemKey: string) => {
    try {
      const items: CaseDisplayGroupItemInput[] = group.items
        .filter((item) => (type === "calc" ? item.caseCalcKey !== itemKey : item.casePropertyKey !== itemKey))
        .map((i) => ({
          caseCustomPropertyKey: i.casePropertyKey,
          caseCalcPropertyKey: i.caseCalcKey,
          lensId,
          rank: i.rank,
          createdAt: i.createdAt,
          rowIndex: i.rowIndex,
          colIndex: i.colIndex,
        }));
      await dispatch(
        updateCaseDisplayGroupWithItems({
          lensId,
          id: group.id,
          name: group.name,
          description: group.description,
          type: group.type,
          columns: group.columns,
          rows: group.rows,
          items,
        })
      );
    } catch (e) {
      console.error(e);
      enqueueSnackbar("Unable to remove item", { variant: "error" });
    }
  };

  const addItem = async (type: ItemType, key: string) => {
    try {
      const items: CaseDisplayGroupItemInput[] = group.items.map((i) => ({
        caseCustomPropertyKey: i.casePropertyKey,
        caseCalcPropertyKey: i.caseCalcKey,
        lensId,
        rank: i.rank,
        createdAt: i.createdAt,
        rowIndex: i.rowIndex,
        colIndex: i.colIndex,
      }));
      await dispatch(
        updateCaseDisplayGroupWithItems({
          lensId,
          id: group.id,
          name: group.name,
          description: group.description,
          type: group.type,
          columns: group.columns,
          rows: group.rows,
          items: items.concat([
            {
              caseCalcPropertyKey: type === "calc" ? key : null,
              caseCustomPropertyKey: type === "property" ? key : null,
              lensId,
              rank: (items.slice(-1)[0]?.rank ?? -1) + 1,
            },
          ]),
        })
      );
    } catch (e) {
      console.error(e);
      enqueueSnackbar("Unable to add item", { variant: "error" });
    }
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination || group.items.length === 0) {
      return;
    }

    const reorderedItems = swapElements<CaseDisplayGroupItem>(
      displayItems,
      result.source.index,
      result.destination.index
    );

    snackpatch(
      updateCaseDisplayGroupWithItems({
        lensId,
        id: group.id,
        name: group.name,
        type: group.type,
        columns: group.columns,
        rows: group.rows,
        items: reorderedItems.map((p, i) => ({
          caseCustomPropertyKey: p.casePropertyKey,
          caseCalcPropertyKey: p.caseCalcKey,
          lensId,
          rank: i,
          createdAt: p.createdAt,
        })),
      }),
      {
        successMessage: "Case properties reordered successfully",
        errorMessage: "Unable to reorder case properties",
      }
    );
  };

  useEffect(() => {
    setDisplayItems((group?.items || []).slice().sort((a, b) => a.rank - b.rank));
  }, [group]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="itemDrop">
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={{
                background: snapshot.isDraggingOver ? theme.palette.grey[100] : "white",
                overflow: "auto",
              }}>
              <List
                sx={{
                  p: 0,
                }}>
                {displayItems.map((item, index) => {
                  return (
                    <Draggable key={item.id} draggableId={item.id.toString()} index={index}>
                      {(provided, snapshot) => (
                        <div ref={provided.innerRef} {...provided.draggableProps}>
                          <Box
                            sx={{
                              borderBottom:
                                index === displayItems.length - 1 ? "none" : `1px solid ${theme.palette.grey[100]}`,
                              backgroundColor: snapshot.isDragging ? theme.palette.primary.light : "white",
                              borderRadius: snapshot.isDragging ? 1 : 0,
                              display: "flex",
                              alignItems: "center",
                              p: 1,
                            }}>
                            {displayItems.length > 1 && (
                              <IconButton size="small">
                                <span {...provided.dragHandleProps}>
                                  <DragIndicator />
                                </span>
                              </IconButton>
                            )}

                            <ListItem>
                              {item.casePropertyKey !== undefined && item.casePropertyKey !== null ? (
                                <CasePropertyPreview item={item} onRemove={removeItem} />
                              ) : (
                                <CaseCalcPreview item={item} onRemove={removeItem} />
                              )}
                            </ListItem>
                          </Box>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
              </List>
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <DropdownButton<string>
        title="Add Item"
        onSelect={(itemId) => {
          const [itemType, itemKey] = itemId.split("-");
          addItem(itemType === "calc" ? "calc" : "property", itemKey as string);
        }}
        options={buttonItems}
      />
    </Box>
  );
}

function TableColumnHeader({
  column,
  columnDescription,
  updateColumn,
}: {
  column: string;
  columnDescription?: string;
  updateColumn: (column: string, columnDescription?: string) => void;
}) {
  const popover = usePopover();
  const [c, setColumn] = useState(column);
  const [cDescription, setDescription] = useState<string>(columnDescription ?? "");
  const canSave = c !== "";

  const save = async () => {
    updateColumn(c, cDescription);
    popover.onPopoverClose();
  };

  return (
    <>
      <div className="flex gap-2">
        <p
          className="text-xs cursor-pointer hover:underline"
          onClick={(evt) => popover.onPopoverOpen(evt.currentTarget)}>
          {column}
        </p>
        {columnDescription !== undefined && <DescriptionTooltip description={columnDescription} />}
      </div>
      <Popover
        open={popover.isOpen}
        anchorEl={popover.anchorEl}
        onClose={popover.onPopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        sx={{ maxWidth: 1200 }}>
        <div className="flex flex-col gap-2 p-4 min-w-80">
          <FormControl fullWidth>
            <TextField label="Column" value={c} onChange={(evt) => setColumn(evt.target.value)} />
          </FormControl>
          <FormControl fullWidth>
            <TextField label="Description" value={cDescription} onChange={(evt) => setDescription(evt.target.value)} />
          </FormControl>
          <Button variant="contained" disabled={!canSave} onClick={save}>
            Save
          </Button>
        </div>
      </Popover>
    </>
  );
}

function TableRowHeader({
  row,
  rowDescription,
  updateRow,
}: {
  row: string;
  rowDescription?: string;
  updateRow: (row: string, rowDescription?: string) => void;
}) {
  const popover = usePopover();
  const [r, setRow] = useState(row);
  const [rDescription, setDescription] = useState<string>(rowDescription ?? "");
  const canSave = r !== "";

  const save = async () => {
    updateRow(r, rDescription);
    popover.onPopoverClose();
  };

  return (
    <>
      <div className="flex gap-2">
        <p
          className="text-xs cursor-pointer hover:underline"
          onClick={(evt) => popover.onPopoverOpen(evt.currentTarget)}>
          {row}
        </p>
        {rowDescription !== undefined && <DescriptionTooltip description={rowDescription} />}
      </div>
      <Popover
        open={popover.isOpen}
        anchorEl={popover.anchorEl}
        onClose={popover.onPopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        sx={{ maxWidth: 1200 }}>
        <div className="flex flex-col gap-2 p-4 min-w-80">
          <FormControl fullWidth>
            <TextField label="Column" value={r} onChange={(evt) => setRow(evt.target.value)} />
          </FormControl>
          <FormControl fullWidth>
            <TextField label="Description" value={rDescription} onChange={(evt) => setDescription(evt.target.value)} />
          </FormControl>
          <Button variant="contained" disabled={!canSave} onClick={save}>
            Save
          </Button>
        </div>
      </Popover>
    </>
  );
}

function TableTypeGroup({ lensId, group }: { lensId: number; group: CDG }) {
  const [addingRow, setAddingRow] = useState(false);
  const [addingColumn, setAddingColumn] = useState(false);
  const [columns, setColumns] = useState<string[]>(group.columns ?? []);
  const [columnDescriptions, setColumnDescriptions] = useState<string[]>(group.columnDescriptions ?? []);
  const [rowDescriptions, setRowDescriptions] = useState<string[]>(group.rowDescriptions ?? []);
  const [rows, setRows] = useState<string[]>(group.rows ?? []);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const confirmCtx = useContext(ConfirmationDialogContext);

  const updateGroup = async ({
    newRows,
    newColumns,
    newColumnDescriptions,
    newRowDescriptions,
  }: {
    newRows: string[];
    newColumns: string[];
    newColumnDescriptions: string[];
    newRowDescriptions: string[];
  }) => {
    try {
      await dispatch(
        updateCaseDisplayGroups({
          lensId,
          groups: [
            {
              columns: newColumns,
              description: group.description,
              columnDescriptions: newColumnDescriptions,
              rowDescriptions: newRowDescriptions,
              id: group.id,
              name: group.name,
              rows: newRows,
            },
          ],
        })
      ).unwrap();
      setAddingColumn(false);
      setAddingRow(false);
    } catch (e) {
      console.error(e);
      enqueueSnackbar((e as { message: string }).message ?? "Unable to update group", { variant: "error" });
      throw e;
    }
  };

  const updateGroupAndItems = async (
    shiftedItems: CaseDisplayGroupItemInput[],
    rows: string[],
    columns: string[],
    columnDescriptions?: string[],
    rowDescriptions?: string[]
  ) => {
    try {
      await dispatch(
        updateCaseDisplayGroupWithItems({
          lensId,
          id: group.id,
          name: group.name,
          type: group.type,
          description: group.description,
          rows,
          columns,
          columnDescriptions,
          rowDescriptions,
          items: shiftedItems,
        })
      ).unwrap();
    } catch (e) {
      console.error(e);
      enqueueSnackbar("Update failed", { variant: "error" });
    }
  };

  const addRow = async (row: string) => {
    const newRows = [...rows, row];
    let newRowDescriptions: string[] = [];
    if (rowDescriptions.length > 0) {
      newRowDescriptions = [...rowDescriptions, ""];
    } else {
      newRowDescriptions = newRows.map(() => "");
    }
    try {
      await updateGroup({
        newRows,
        newColumns: columns,
        newRowDescriptions,
        newColumnDescriptions: columnDescriptions,
      });
      setRows(newRows);
      setRowDescriptions(newRowDescriptions);
    } catch (e) {
      // no-op
    }
  };

  const addColumn = async (column: string) => {
    const newColumns = [...columns, column];
    let newColumnDescriptions: string[] = [];
    if (columnDescriptions.length > 0) {
      newColumnDescriptions = [...columnDescriptions, ""];
    } else {
      newColumnDescriptions = newColumns.map(() => "");
    }
    try {
      await updateGroup({ newRows: rows, newColumns, newColumnDescriptions, newRowDescriptions: rowDescriptions });
      setColumns(newColumns);
      setColumnDescriptions(newColumnDescriptions);
    } catch (e) {
      // no-op
    }
  };

  const shiftColumn = async (colIndex: number, dir: "left" | "right") => {
    const newColIndex = colIndex + (dir === "left" ? -1 : 1);
    const shiftedColumns = swapElements<string>(columns, colIndex, newColIndex);
    const shiftedColumnDescriptions = swapElements<string>(columnDescriptions, colIndex, newColIndex);
    setColumns(shiftedColumns);
    setColumnDescriptions(shiftedColumnDescriptions);
    const items: CaseDisplayGroupItemInput[] = group.items.map((item) => ({
      rank: item.rank,
      caseCalcPropertyKey: item.caseCalcKey,
      caseCustomPropertyKey: item.casePropertyKey,
      rowIndex: item.rowIndex,
      colIndex: item.colIndex === colIndex ? newColIndex : item.colIndex === newColIndex ? colIndex : item.colIndex,
      lensId,
    }));

    await updateGroupAndItems(items, rows, shiftedColumns, shiftedColumnDescriptions, rowDescriptions);
  };

  const shiftRow = async (rowIndex: number, dir: "up" | "down") => {
    const newRowIndex = rowIndex + (dir === "up" ? -1 : 1);
    const shiftedRows = swapElements<string>(rows, rowIndex, newRowIndex);
    const shiftedRowDescriptions = swapElements<string>(rowDescriptions, rowIndex, newRowIndex);
    setRows(shiftedRows);
    setRowDescriptions(shiftedRowDescriptions);
    const items: CaseDisplayGroupItemInput[] = group.items.map((item) => ({
      rank: item.rank,
      caseCalcPropertyKey: item.caseCalcKey,
      caseCustomPropertyKey: item.casePropertyKey,
      colIndex: item.colIndex,
      rowIndex: item.rowIndex === rowIndex ? newRowIndex : item.rowIndex === newRowIndex ? rowIndex : item.rowIndex,
      lensId,
    }));

    await updateGroupAndItems(items, shiftedRows, columns, columnDescriptions, shiftedRowDescriptions);
  };

  const deleteCol = (colIndex: number, column: string) => {
    confirmCtx.confirm({
      title: `Remove Column`,
      prompt: <P variant="sm">Are you sure you want to delete {column}?</P>,
      callback: async () => {
        const columnsCopy = columns.slice();
        const columnDescriptionsCopy = columnDescriptions.slice();
        columnsCopy.splice(colIndex, 1);
        columnDescriptionsCopy.splice(colIndex, 1);
        setColumns(columnsCopy);
        setColumnDescriptions(columnDescriptionsCopy);
        const items: CaseDisplayGroupItemInput[] = group.items
          .filter((item) => item.colIndex !== colIndex)
          .map((item) => ({
            rank: item.rank,
            caseCalcPropertyKey: item.caseCalcKey,
            caseCustomPropertyKey: item.casePropertyKey,
            colIndex: item.colIndex,
            rowIndex: item.rowIndex,
            lensId,
          }));

        await updateGroupAndItems(items, rows, columnsCopy, columnDescriptionsCopy, rowDescriptions);
      },
    });
  };

  const deleteRow = (rowIndex: number, row: string) => {
    confirmCtx.confirm({
      title: `Remove Row`,
      prompt: <P variant="sm">Are you sure you want to delete {row}?</P>,
      callback: async () => {
        const rowsCopy = rows.slice();
        const rowDescriptionsCopy = rowDescriptions.slice();
        rowsCopy.splice(rowIndex, 1);
        rowDescriptionsCopy.splice(rowIndex, 1);
        setRows(rowsCopy);
        setRowDescriptions(rowDescriptionsCopy);
        const items: CaseDisplayGroupItemInput[] = group.items
          .filter((item) => item.rowIndex !== rowIndex)
          .map((item) => ({
            rank: item.rank,
            caseCalcPropertyKey: item.caseCalcKey,
            caseCustomPropertyKey: item.casePropertyKey,
            colIndex: item.colIndex,
            rowIndex: item.rowIndex,
            lensId,
          }));

        await updateGroupAndItems(items, rowsCopy, columns, columnDescriptions, rowDescriptionsCopy);
      },
    });
  };

  const updateColumn = async (column: string, columnIndex: number, columnDescription?: string) => {
    const newColumns = columns.slice();
    newColumns[columnIndex] = column;
    const newColumnDescriptions = (columnDescriptions ?? []).slice();

    if (columnDescription !== undefined) {
      if (newColumnDescriptions.length === 0) {
        newColumns.forEach((_, index) => {
          newColumnDescriptions[index] = index === columnIndex ? columnDescription : "";
        });
      } else {
        newColumnDescriptions[columnIndex] = columnDescription;
      }
    }
    setColumns(newColumns);
    setColumnDescriptions(newColumnDescriptions);

    const items: CaseDisplayGroupItemInput[] = group.items.map((item) => ({
      rank: item.rank,
      caseCalcPropertyKey: item.caseCalcKey,
      caseCustomPropertyKey: item.casePropertyKey,
      rowIndex: item.rowIndex,
      colIndex: item.colIndex,
      lensId,
    }));

    await updateGroupAndItems(items, rows, newColumns, newColumnDescriptions, rowDescriptions);
  };

  const updateRow = async (row: string, rowIndex: number, rowDescription?: string) => {
    const newRows = rows.slice();
    newRows[rowIndex] = row;
    const newRowDescriptions = (rowDescriptions ?? []).slice();

    if (rowDescription !== undefined) {
      if (newRowDescriptions.length === 0) {
        newRows.forEach((_, index) => {
          newRowDescriptions[index] = index === rowIndex ? rowDescription : "";
        });
      } else {
        newRowDescriptions[rowIndex] = rowDescription;
      }
    }
    setRows(newRows);
    setRowDescriptions(newRowDescriptions);

    const items: CaseDisplayGroupItemInput[] = group.items.map((item) => ({
      rank: item.rank,
      caseCalcPropertyKey: item.caseCalcKey,
      caseCustomPropertyKey: item.casePropertyKey,
      rowIndex: item.rowIndex,
      colIndex: item.colIndex,
      lensId,
    }));

    await updateGroupAndItems(items, newRows, columns, columnDescriptions, newRowDescriptions);
  };

  return (
    <Card variant="outlined">
      <TableContainer>
        <Table
          sx={{
            " th, td": {
              whiteSpace: "nowrap",
            },
            overflow: "auto",
          }}>
          <TableHead>
            <TableRow sx={{ height: 50 }}>
              <TableCell sx={{ ...LeftStickyColumnSX }} />
              {columns.map((c, colIndex) => (
                <TableCell key={c}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      ":hover": {
                        " .invisible": {
                          visibility: "visible",
                        },
                      },
                    }}>
                    <div className="invisible">
                      <IconButton disabled={colIndex === 0} onClick={() => shiftColumn(colIndex, "left")}>
                        <ArrowLeft />
                      </IconButton>
                    </div>
                    <TableColumnHeader
                      column={c}
                      columnDescription={columnDescriptions[colIndex]}
                      updateColumn={(column, description) => updateColumn(column, colIndex, description)}
                    />
                    {colIndex !== columns.length - 1 && (
                      <div className="invisible">
                        <IconButton onClick={() => shiftColumn(colIndex, "right")}>
                          <ArrowRight />
                        </IconButton>
                      </div>
                    )}
                    {colIndex === columns.length - 1 && (
                      <div className="invisible">
                        <IconButton color="error" onClick={() => deleteCol(colIndex, c)}>
                          <Delete />
                        </IconButton>
                      </div>
                    )}
                  </Box>
                </TableCell>
              ))}
              <TableCell>
                {addingColumn ? (
                  <FormControl sx={{ width: 150 }}>
                    <TextField
                      autoFocus
                      label="New Column"
                      onChange={(evt) => {
                        if (evt.target.value === "") {
                          setAddingColumn(false);
                        }
                      }}
                      onKeyUp={(evt) => {
                        const column = (evt.target as HTMLInputElement).value;
                        if (evt.key === "Enter" && column !== "") {
                          evt.preventDefault();
                          addColumn(column);
                        }
                      }}
                    />
                  </FormControl>
                ) : (
                  <Button size="small" variant="text" onClick={() => setAddingColumn(true)}>
                    Add Column
                  </Button>
                )}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRow key={row}>
                <TableCell sx={{ ...LeftStickyColumnSX }}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      ":hover": {
                        " .invisible": {
                          visibility: "visible",
                        },
                      },
                    }}>
                    <div className="invisible">
                      <IconButton disabled={rowIndex === 0} onClick={() => shiftRow(rowIndex, "up")}>
                        <ArrowDropUp />
                      </IconButton>
                    </div>
                    <TableRowHeader
                      row={row}
                      rowDescription={rowDescriptions[rowIndex]}
                      updateRow={(row, description) => updateRow(row, rowIndex, description)}
                    />
                    {rowIndex !== rows.length - 1 && (
                      <div className="invisible">
                        <IconButton onClick={() => shiftRow(rowIndex, "down")}>
                          <ArrowDropDown />
                        </IconButton>
                      </div>
                    )}
                    {rowIndex === rows.length - 1 && (
                      <div className="invisible">
                        <IconButton color="error" onClick={() => deleteRow(rowIndex, row)}>
                          <Delete />
                        </IconButton>
                      </div>
                    )}
                  </Box>
                </TableCell>
                {columns.map((c, colIndex) => (
                  <ItemTableCell key={row + c} colIndex={colIndex} rowIndex={rowIndex} lensId={lensId} group={group} />
                ))}
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={columns.length + 1}>
                {addingRow ? (
                  <FormControl>
                    <TextField
                      autoFocus
                      label="New Row"
                      onChange={(evt) => {
                        if (evt.target.value === "") {
                          setAddingRow(false);
                        }
                      }}
                      onKeyUp={(evt) => {
                        const row = (evt.target as HTMLInputElement).value;
                        if (evt.key === "Enter" && row !== "") {
                          evt.preventDefault();
                          addRow(row);
                        }
                      }}
                    />
                  </FormControl>
                ) : (
                  <Button size="small" variant="text" onClick={() => setAddingRow(true)}>
                    Add Row
                  </Button>
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}

function DeleteGroup({ group, lensId }: { group: CDG; lensId: number }) {
  const snackpatch = useAppDispatchWithSnackbar();
  const confirmCtx = useContext(ConfirmationDialogContext);
  const navigate = useNavigate();

  const onGroupDelete = () => {
    confirmCtx.confirm({
      title: `Delete ${group.name}`,
      prompt: <P variant="sm">Are you sure you want to delete this group?</P>,
      callback: async () => {
        await snackpatch(
          deleteCaseDisplayGroup({
            lensId: lensId,
            groupId: group.id,
          }),
          {
            successMessage: "Group deleted",
            errorMessage: "Unable to delete group",
          }
        );
        navigate(AppRoutes.lensEditorCasePropertiesGroups(lensId));
      },
    });
  };

  return (
    <Card sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 1,
          py: 8,
        }}>
        <P variant="xl2Bold">Empty Group</P>

        <Box sx={{ mt: 1 }}>
          {group.name !== "Default" && (
            <Button variant="outlined" color="error" onClick={onGroupDelete}>
              Delete Group
            </Button>
          )}
        </Box>
      </Box>
    </Card>
  );
}

export default function CaseDisplayGroup() {
  const caseDisplayGroups = useAppSelector(selectCaseDisplayGroups);
  const { caseDisplayGroupName, lensId } = useParams();
  const safeLensId = parseSafeInt(lensId);
  const group = caseDisplayGroups.find((cdg) => cdg.name === caseDisplayGroupName);

  if (group === undefined || safeLensId === undefined) {
    return <Navigate to=".." />;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <P variant="baseBold">{group.name}</P>
      {group.type === CaseDisplayGroupType.Card ? (
        <CardTypeGroup key={group.id} lensId={safeLensId} group={group} />
      ) : (
        <TableTypeGroup key={group.id} lensId={safeLensId} group={group} />
      )}
      {group.items.length === 0 && <DeleteGroup group={group} lensId={safeLensId} />}
    </Box>
  );
}
